<template>
  <div class="d-flex flex-column-fluid business-insights-page late-fee-detail-page">
    <div class="container">
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">{{ fee.reference || "Late Fee" }}</h1>
            <p class="subtitle">
              {{ periodLabel }}
              <span v-if="fee.business_name"> · {{ fee.business_name }}</span>
              <span v-if="fee.invoice_reference"> · {{ fee.invoice_reference }}</span>
            </p>
          </div>
          <div class="header-actions">
            <button
              type="button"
              class="header-btn header-btn-preview"
              :disabled="!fee.id || loadingPdf"
              @click="previewPdf"
            >
              <i class="las la-eye"></i>
              <span>{{ loadingPdf ? "Loading..." : "Preview" }}</span>
            </button>
            <button
              type="button"
              class="header-btn header-btn-download"
              :disabled="!fee.id || loadingPdf"
              @click="downloadPdf"
            >
              <i class="las la-download"></i>
              <span>Download</span>
            </button>
            <button type="button" class="header-btn header-btn-back" @click="goBack">
              <i class="las la-arrow-left"></i>
              <span>Back</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading fee details...</p>
      </div>
      <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-else class="detail-content">
        <section class="summary-grid">
          <div class="summary-card">
            <div class="summary-label">Monthly rate</div>
            <div class="summary-value">{{ formatRate(fee.monthly_rate || fee.rate) }}% / month</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Daily rate (display)</div>
            <div class="summary-value">{{ dailyRateDisplay }}% / day</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Factoring invoice</div>
            <div class="summary-value">{{ fee.invoice_reference || "-" }}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Billed days</div>
            <div class="summary-value">{{ fee.total_days || 0 }}</div>
          </div>
          <div class="summary-card highlight">
            <div class="summary-label">Total late fee</div>
            <div class="summary-value">
              {{ formatFullNumber(fee.fee_amount) }}<sup>{{ fee.currency }}</sup>
            </div>
          </div>
        </section>

        <section class="formula-card">
          <h3>How this invoice is calculated</h3>
          <p class="formula">
            Fee per segment = Outstanding × Days × (Monthly rate / 100) / 30
          </p>
          <ul>
            <li>Days follow Excel DAYS() (payment date minus previous date, not inclusive of both ends).</li>
            <li>Accrual starts on the factoring invoice due date and splits on each repayment.</li>
            <li>Each segment is rounded to 2 decimals (half up), then summed.</li>
            <li>The daily % shown on the invoice is monthly rate / 30 at 2 decimals ({{ dailyRateDisplay }}%).</li>
          </ul>
        </section>

        <section class="activity-section">
          <div class="table-card">
            <div class="card-header">
              <h3 class="card-title">Outstanding amount summary</h3>
            </div>
            <div v-if="!outstandingRows.length" class="empty-state">
              <p>No outstanding schedule for this invoice.</p>
            </div>
            <div v-else class="activity-table-wrapper">
              <table class="activity-table">
                <thead>
                  <tr>
                    <th>Reporting date</th>
                    <th class="amount-header">Due amount</th>
                    <th class="amount-header">Repayment</th>
                    <th class="amount-header">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in outstandingRows" :key="'out-' + index">
                    <td>{{ formatLongDate(row.date) }}</td>
                    <td class="amount-cell">
                      {{ formatFullNumber(row.due_amount) }}<sup>{{ fee.currency }}</sup>
                    </td>
                    <td class="amount-cell">
                      <template v-if="row.repayment == null">-</template>
                      <template v-else>
                        {{ formatFullNumber(row.repayment) }}<sup>{{ fee.currency }}</sup>
                      </template>
                    </td>
                    <td class="amount-cell">
                      <template v-if="row.outstanding == null">-</template>
                      <template v-else>
                        {{ formatFullNumber(row.outstanding) }}<sup>{{ fee.currency }}</sup>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="activity-section">
          <div class="table-card">
            <div class="card-header">
              <h3 class="card-title">Late fee summary</h3>
            </div>
            <div v-if="!segments.length" class="empty-state">
              <p>No late-fee segments for this invoice.</p>
            </div>
            <div v-else class="activity-table-wrapper">
              <table class="activity-table">
                <thead>
                  <tr>
                    <th>Accrued date</th>
                    <th>Description</th>
                    <th class="amount-header">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in segments" :key="'fee-' + index">
                    <td>{{ formatLongDate(row.accrued_date || row.to_date) }}</td>
                    <td class="formula-cell">
                      = {{ dailyRateDisplay }}% × {{ fee.currency }} {{ formatFullNumber(row.outstanding) }}
                      × {{ row.days }} {{ row.days === 1 ? "day" : "days" }}
                    </td>
                    <td class="amount-cell">
                      {{ formatFullNumber(row.fee) }}<sup>{{ fee.currency }}</sup>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="2" class="total-label">Total late fee {{ fee.currency }}</td>
                    <td class="amount-cell total-amount">
                      {{ formatFullNumber(fee.fee_amount) }}<sup>{{ fee.currency }}</sup>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>

    <pdf-viewer
      v-if="showPDF"
      :url="pdfUrl"
      :name="pdfName"
      :showHeader="true"
      :forceExpansion="true"
      :enableOverlayEffect="true"
      @closePDF="closePDFViewer"
    />
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import PdfViewer from "@/components/pdfViewer.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "LateFeeDetail",
  components: { PdfViewer },
  data() {
    return {
      loading: true,
      error: null,
      fee: {},
      showPDF: false,
      pdfUrl: null,
      pdfName: "",
      loadingPdf: false,
    };
  },
  computed: {
    feeId() {
      return this.$route.params.fee_id;
    },
    segments() {
      return this.fee.segments || [];
    },
    outstandingRows() {
      return this.fee.outstanding_rows || [];
    },
    periodLabel() {
      if (!this.fee.period_start) return "";
      const start = this.formatLongDate(this.fee.period_start);
      const end = this.formatLongDate(this.fee.period_end);
      return start && end ? `${start} – ${end}` : start;
    },
    dailyRateDisplay() {
      const monthly = Number(this.fee.monthly_rate || this.fee.rate);
      if (Number.isNaN(monthly)) return "-";
      return (monthly / 30).toFixed(2);
    },
  },
  async mounted() {
    await this.loadFee();
  },
  beforeDestroy() {
    this.closePDFViewer();
  },
  methods: {
    formatFullNumber(val) {
      if (val === undefined || val === null || val === "" || Number.isNaN(Number(val))) return "-";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatRate(val) {
      if (val === undefined || val === null || val === "") return "-";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    },
    formatLongDate(value) {
      if (!value) return "-";
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    async loadFee() {
      this.loading = true;
      this.error = null;
      try {
        const response = await FinancingService.getLateFee(this.feeId);
        this.fee = response.data || {};
      } catch (error) {
        this.error = formatErrorMessage(error) || "Failed to load late fee.";
      } finally {
        this.loading = false;
      }
    },
    async fetchPdfBlob() {
      this.loadingPdf = true;
      try {
        return await FinancingService.getLateFeePdf(this.feeId);
      } finally {
        this.loadingPdf = false;
      }
    },
    async previewPdf() {
      try {
        const blob = await this.fetchPdfBlob();
        this.pdfUrl = URL.createObjectURL(blob);
        this.pdfName = `${this.fee.reference || "late-fee"}.pdf`;
        this.showPDF = true;
      } catch (error) {
        this.$showAlert(formatErrorMessage(error) || "Failed to load PDF", "danger");
      }
    },
    async downloadPdf() {
      try {
        const blob = await this.fetchPdfBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.fee.reference || "late-fee"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$showAlert(formatErrorMessage(error) || "Failed to download PDF", "danger");
      }
    },
    closePDFViewer() {
      this.showPDF = false;
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.pdfName = "";
    },
    goBack() {
      const businessId = this.$route.query.business_id || this.fee.business_id;
      if (businessId) {
        this.$router.push({
          name: "business-insights",
          params: { business_id: businessId },
        });
        return;
      }
      this.$router.push({ name: "financing-late-fees" });
    },
  },
};
</script>

<style scoped lang="scss">
@import "./BusinessInsights.scss";
@import "./NonUseFeeDetail.scss";
</style>
